import config from './config';

function authHeaders(token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function apiFetch(path, { method = 'GET', body, token } = {}) {
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: authHeaders(token),
    credentials: 'include',
    body: body == null ? undefined : JSON.stringify(body),
  });
  const data = await parseJson(res);
  return { res, data };
}

export async function loginWithPassword(username, password) {
  const body = new URLSearchParams({
    grant_type: 'password',
    username,
    password,
  });
  const res = await fetch(`${config.apiBaseUrl}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    credentials: 'include',
    body,
  });
  if (!res.ok) {
    throw new Error('Sign-in failed. Check your credentials.');
  }
  const data = await res.json().catch(() => null);
  const token = typeof data === 'string' ? data : data?.access_token;
  if (!token) throw new Error('Sign-in failed. No access token returned.');
  return token;
}

export async function setSharedCookie(token) {
  await apiFetch('/api/payroll/auth/cookie', { method: 'POST', token });
}

export async function logoutShared() {
  await apiFetch('/api/payroll/auth/logout', { method: 'POST' });
}

export async function fetchMe(token) {
  return apiFetch('/api/payroll/auth/me', { token });
}

export async function signupPayroll({ email, password, confirmPassword, displayName }) {
  return apiFetch('/api/payroll/auth/signup', {
    method: 'POST',
    body: { email, password, confirmPassword, displayName },
  });
}

export async function createStandaloneTenant(token, displayName) {
  return apiFetch('/api/payroll/tenants/standalone', {
    method: 'POST',
    token,
    body: { displayName },
  });
}

export async function updateTenantSettings(token, payload) {
  return apiFetch('/api/payroll/tenants/current', {
    method: 'PUT',
    token,
    body: payload,
  });
}

export async function listEmployees(token) {
  return apiFetch('/api/payroll/employees', { token });
}

export async function createEmployee(token, payload) {
  return apiFetch('/api/payroll/employees', { method: 'POST', token, body: payload });
}

export async function updateEmployee(token, employeeId, payload) {
  return apiFetch(`/api/payroll/employees/${encodeURIComponent(employeeId)}`, {
    method: 'PUT',
    token,
    body: payload,
  });
}

export async function deleteEmployee(token, employeeId) {
  return apiFetch(`/api/payroll/employees/${encodeURIComponent(employeeId)}`, {
    method: 'DELETE',
    token,
  });
}

export async function getEmployeeTaxProfile(token, employeeId) {
  return apiFetch(`/api/payroll/employees/${encodeURIComponent(employeeId)}/tax-profile`, { token });
}

export async function saveEmployeeTaxProfile(token, employeeId, profile) {
  return apiFetch(`/api/payroll/employees/${encodeURIComponent(employeeId)}/tax-profile`, {
    method: 'PUT',
    token,
    body: profile,
  });
}

export async function fetchEngineStatus(token) {
  return apiFetch('/api/payroll/engine/status', { token });
}

export async function fetchEngineCatalog(token) {
  return apiFetch('/api/payroll/engine/catalog', { token });
}

export async function peProxy(token, method, path, body) {
  const rel = String(path || '').replace(/^\/api\//, '').replace(/^\//, '');
  return apiFetch(`/api/payroll/engine/pe/${rel}`, {
    method: method || 'GET',
    token,
    body,
  });
}

export async function saveEngineSettings(token, payload) {
  return apiFetch('/api/payroll/engine/settings', {
    method: 'PUT',
    token,
    body: payload,
  });
}

export async function previewPayPeriod(token, start, end) {
  const q = new URLSearchParams({ start, end });
  return apiFetch(`/api/payroll/pay-periods/preview?${q}`, { token });
}

export async function savePayPeriod(token, { startDate, endDate, rows }) {
  return apiFetch('/api/payroll/pay-periods/current', {
    method: 'PUT',
    token,
    body: { startDate, endDate, rows },
  });
}

export async function syncPayPeriod(token, { startDate, endDate, rows }) {
  return apiFetch('/api/payroll/pay-periods/sync', {
    method: 'POST',
    token,
    body: { startDate, endDate, rows },
  });
}

export async function previewPayrun(token, start, end) {
  const q = new URLSearchParams({ start, end });
  return apiFetch(`/api/payroll/payruns/preview?${q}`, { token });
}

export async function listPayruns(token) {
  return apiFetch('/api/payroll/payruns', { token });
}

export async function createPayrun(token, payload) {
  return apiFetch('/api/payroll/payruns', {
    method: 'POST',
    token,
    body: payload,
  });
}

export async function export941Worksheet(token, payrunId) {
  return apiFetch(`/api/payroll/payruns/${encodeURIComponent(payrunId)}/exports/941-worksheet`, { token });
}

export async function exportW2Worksheet(token, payrunId, year) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return apiFetch(`/api/payroll/payruns/${encodeURIComponent(payrunId)}/exports/w2-worksheet${q}`, { token });
}

export async function fetchComplianceOverview(token, start, end) {
  const q = new URLSearchParams();
  if (start) q.set('start', start);
  if (end) q.set('end', end);
  const qs = q.toString();
  return apiFetch(`/api/payroll/compliance/overview${qs ? `?${qs}` : ''}`, { token });
}

export async function fetchFilingsOverview(token, year) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return apiFetch(`/api/payroll/filings/overview${q}`, { token });
}

export async function fetch941Quarter(token, year, quarter) {
  const q = new URLSearchParams();
  if (year) q.set('year', year);
  if (quarter) q.set('quarter', quarter);
  return apiFetch(`/api/payroll/filings/941?${q}`, { token });
}

export async function fetchW2Year(token, year) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return apiFetch(`/api/payroll/filings/w2${q}`, { token });
}

export async function fetchW3Year(token, year) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return apiFetch(`/api/payroll/filings/w3${q}`, { token });
}

export async function markFiling(token, obligationId, payload) {
  return apiFetch(`/api/payroll/filings/${encodeURIComponent(obligationId)}/mark`, {
    method: 'POST',
    token,
    body: payload,
  });
}